"use client";

import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import {
  PageContainer,
  ContentContainer,
  Title,
  LastUpdated,
  Section,
  SectionTitle,
  SubsectionTitle,
  Paragraph,
  List,
} from "./page.styles";

export default function TermsPage() {
  return (
    <PageContainer>
      <PublicHeader />
      <ContentContainer>
        <Title>Terms & Conditions</Title>
        <LastUpdated>Last updated March 08, 2023</LastUpdated>

        <Section>
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <Paragraph>
            By accessing and using Drizl (&quot;the Platform&quot;), you accept
            and agree to be bound by the terms and provision of this agreement.
            If you do not agree to these Terms & Conditions, please do not use
            the Platform.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Definitions</SectionTitle>
          <Paragraph>
            <strong>&quot;Artisan&quot;</strong> refers to individuals who
            prepare and offer homemade meals through the Platform.
          </Paragraph>
          <Paragraph>
            <strong>&quot;Consumer&quot;</strong> refers to individuals who
            purchase meals from Artisans through the Platform.
          </Paragraph>
          <Paragraph>
            <strong>&quot;Platform&quot;</strong> refers to the Drizl website,
            mobile application, and all related services.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>3. User Accounts</SectionTitle>
          <SubsectionTitle>3.1 Account Creation</SubsectionTitle>
          <List>
            <li>You must be at least 18 years old to create an account</li>
            <li>
              You must provide accurate, current, and complete information
            </li>
            <li>
              You are responsible for maintaining the confidentiality of your
              account credentials
            </li>
            <li>
              You are responsible for all activities that occur under your
              account
            </li>
          </List>

          <SubsectionTitle>3.2 Account Termination</SubsectionTitle>
          <Paragraph>
            We reserve the right to suspend or terminate accounts that violate
            these terms, engage in fraudulent activity, or pose a risk to the
            Platform or its users.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>4. Artisan Responsibilities</SectionTitle>
          <SubsectionTitle>4.1 Food Safety & Compliance</SubsectionTitle>
          <List>
            <li>
              Artisans must comply with all local health and safety regulations
            </li>
            <li>
              Artisans are responsible for obtaining necessary licenses and
              permits
            </li>
            <li>Meals must be prepared in clean, sanitary environments</li>
            <li>
              Artisans must accurately disclose ingredients and potential
              allergens
            </li>
          </List>

          <SubsectionTitle>4.2 Meal Listings</SubsectionTitle>
          <List>
            <li>All meal descriptions must be accurate and truthful</li>
            <li>Photos must accurately represent the actual meal</li>
            <li>Pricing must be clear and include all applicable fees</li>
            <li>Delivery/pickup options must be honored as stated</li>
          </List>

          <SubsectionTitle>4.3 Order Fulfillment</SubsectionTitle>
          <Paragraph>
            Artisans agree to fulfill orders in a timely manner and maintain
            quality standards. Failure to fulfill orders may result in account
            suspension or termination.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Consumer Responsibilities</SectionTitle>
          <List>
            <li>Provide accurate delivery/pickup information</li>
            <li>Be available to receive orders at the agreed-upon time</li>
            <li>
              Review meal descriptions, ingredients, and allergen information
              carefully
            </li>
            <li>Report any issues or concerns promptly</li>
            <li>Treat Artisans with respect and professionalism</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Payments & Fees</SectionTitle>
          <SubsectionTitle>6.1 Platform Fees</SubsectionTitle>
          <Paragraph>
            Drizl charges a service fee on each transaction. This fee is clearly
            displayed before order confirmation and covers platform maintenance,
            payment processing, and customer support.
          </Paragraph>

          <SubsectionTitle>6.2 Payments</SubsectionTitle>
          <List>
            <li>
              All payments are processed securely through our payment partners
            </li>
            <li>Consumers are charged at the time of order placement</li>
            <li>Artisans receive payment after successful order completion</li>
            <li>Refunds are handled according to our refund policy</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>7. Cancellations & Refunds</SectionTitle>
          <SubsectionTitle>7.1 Order Cancellations</SubsectionTitle>
          <Paragraph>
            Consumers may cancel orders within the timeframe specified by the
            Artisan. Late cancellations may not be eligible for refunds.
          </Paragraph>

          <SubsectionTitle>7.2 Refund Policy</SubsectionTitle>
          <Paragraph>
            Refunds are issued at Drizl&apos;s discretion in cases of order
            issues, quality problems, or Artisan failure to fulfill orders.
            Disputes should be reported within 48 hours of order completion.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Intellectual Property</SectionTitle>
          <Paragraph>
            All content on the Platform, including logos, designs, text, and
            software, is the property of Drizl, LLP or its licensors. Users may
            not reproduce, distribute, or create derivative works without
            explicit permission.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. Liability Limitations</SectionTitle>
          <Paragraph>
            <strong>
              Drizl acts as a platform connecting Artisans and Consumers.
            </strong>{" "}
            We are not responsible for:
          </Paragraph>
          <List>
            <li>Food safety, quality, or preparation by Artisans</li>
            <li>Allergic reactions or foodborne illnesses</li>
            <li>Disputes between Artisans and Consumers</li>
            <li>Loss or damage resulting from platform use</li>
          </List>
          <Paragraph>
            Users assume all risks associated with ordering and consuming food
            from home Artisans.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Privacy</SectionTitle>
          <Paragraph>
            Your use of the Platform is also governed by our Privacy Policy.
            Please review our Privacy Policy to understand how we collect, use,
            and protect your information.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. Dispute Resolution</SectionTitle>
          <Paragraph>
            Any disputes arising from these Terms shall be resolved through
            binding arbitration in accordance with the rules of the American
            Arbitration Association. You waive any right to a jury trial or to
            participate in a class action lawsuit.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>12. Changes to Terms</SectionTitle>
          <Paragraph>
            We reserve the right to modify these Terms at any time. Users will
            be notified of significant changes via email or platform
            notification. Continued use of the Platform after changes
            constitutes acceptance of the modified Terms.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>13. Contact Information</SectionTitle>
          <Paragraph>
            For questions about these Terms & Conditions, please contact us at:
          </Paragraph>
          <Paragraph>
            <strong>Drizl, LLP</strong>
            <br />
            Glen Arm, MD 21057
            <br />
            United States
            <br />
            <br />
            <strong>Email:</strong> support@drizl.app
          </Paragraph>
        </Section>
      </ContentContainer>
      <Footer />
    </PageContainer>
  );
}
